import { useRef, useState } from 'react'
import type { DragEvent } from 'react'

function hasFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes('Files')
}

/**
 * Tracks a file drag over a drop zone and forwards dropped files. Uses an
 * enter/leave depth counter so dragging across child elements doesn't flicker
 * the `dragging` flag.
 */
export function useFileDrop(onFiles: (files: FileList) => void) {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)

  const onDragEnter = (event: DragEvent) => {
    if (!hasFiles(event)) return
    event.preventDefault()
    depth.current += 1
    setDragging(true)
  }

  const onDragOver = (event: DragEvent) => {
    if (hasFiles(event)) event.preventDefault()
  }

  const onDragLeave = (event: DragEvent) => {
    if (!hasFiles(event)) return
    depth.current -= 1
    if (depth.current <= 0) {
      depth.current = 0
      setDragging(false)
    }
  }

  const onDrop = (event: DragEvent) => {
    if (!hasFiles(event)) return
    event.preventDefault()
    depth.current = 0
    setDragging(false)
    if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files)
  }

  return {
    dragging,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  }
}
